import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Editor, { type Monaco } from '@monaco-editor/react';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { api } from '../lib/api';
import type { Language, ProblemDetail, RunSamplesResponse } from '@flarecode/shared';
import { LANGUAGE_LABELS, LANGUAGES, VERDICT_LABELS } from '@flarecode/shared';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useProblemDraft } from '../hooks/useProblemDraft';
import { submissionErrorMessage } from '../lib/submission-errors';
import { configureMonaco } from '../lib/editor';

// Phase 4: Python + JavaScript + TypeScript are wired end-to-end.
// cpp, java, go stay selectable in the dropdown but Submit is disabled
// until Phase 4b brings them online.
const ENABLED_LANGUAGES: ReadonlySet<Language> = new Set<Language>([
  'python',
  'javascript',
  'typescript',
]);

/**
 * Monaco bootstrap. Called once per editor mount (Monaco's beforeMount hook).
 * `configureMonaco` is idempotent so double-invocations from dev-mode
 * StrictMode double-mounts don't duplicate completion providers.
 */
function handleEditorBeforeMount(monaco: Monaco): void {
  configureMonaco(monaco);
}

export function ProblemDetailPage() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useCurrentUser();

  const { data, isLoading, error } = useQuery({
    queryKey: ['problem', slug],
    queryFn: () => api.getProblem(slug),
    enabled: !!slug,
  });

  const [language, setLanguage] = useState<Language>('python');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Run state (Phase 7.2 Tier 1B).
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [runResult, setRunResult] = useState<RunSamplesResponse | null>(null);

  const problem = data?.problem;

  // Draft persistence: loads saved code (if signed in) or starter, and
  // debounce-saves edits to the server while typing. Handles language/
  // problem switches internally.
  const { code, setCode, isLoadingDraft, isSaving, lastSavedAt } = useProblemDraft(
    problem,
    language,
    user?.id,
  );

  if (isLoading) return <p>Loading…</p>;
  if (error || !problem) return <p className="error">Problem not found.</p>;

  const p: ProblemDetail = problem;
  const actionEnabled = !!user && ENABLED_LANGUAGES.has(language);
  const canSubmit = actionEnabled && !submitting && !running;
  const canRun = actionEnabled && !submitting && !running;
  const actionTitle = !user
    ? 'Sign in to run or submit'
    : !ENABLED_LANGUAGES.has(language)
      ? `${LANGUAGE_LABELS[language]} isn't wired up yet`
      : undefined;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await api.createSubmission({
        problemId: p.id,
        language,
        source: code,
      });
      navigate(`/submissions/${res.submissionId}`);
    } catch (err) {
      setSubmitError(submissionErrorMessage(err));
      setSubmitting(false);
    }
  }

  async function handleRun() {
    if (!canRun) return;
    setRunning(true);
    setRunError(null);
    setRunResult(null);
    try {
      const res = await api.runSamples({
        problemId: p.id,
        language,
        source: code,
      });
      setRunResult(res);
    } catch (err) {
      setRunError(submissionErrorMessage(err));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="solve-layout">
      <aside className="problem-pane">
        <div className="problem-header">
          <h2>{p.title}</h2>
          <div className="problem-header-chips">
            <span className={`difficulty-chip difficulty--${p.difficulty}`}>{p.difficulty}</span>
            {p.solvedByUser && (
              <span
                className="solved-chip"
                title="You have solved this problem"
                aria-label="Solved"
              >
                ✓ Solved
              </span>
            )}
          </div>
        </div>
        {p.tags.length > 0 && (
          <div className="tag-row">
            {p.tags.map((t) => (
              <span key={t} className="tag-chip">
                {t}
              </span>
            ))}
          </div>
        )}
        <div className="markdown-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{p.descriptionMarkdown}</ReactMarkdown>
        </div>
        {p.sampleCases.length > 0 && (
          <div className="samples">
            <h3>Sample Cases</h3>
            {p.sampleCases.map((sc, i) => (
              <div key={i} className="sample-case">
                <div className="sample-col">
                  <label>Input</label>
                  <pre>{sc.input.trimEnd()}</pre>
                </div>
                <div className="sample-col">
                  <label>Expected</label>
                  <pre>{sc.expected.trimEnd()}</pre>
                </div>
              </div>
            ))}
          </div>
        )}
      </aside>
      <section className="editor-pane">
        <div className="editor-toolbar">
          <select value={language} onChange={(e) => setLanguage(e.target.value as Language)}>
            {LANGUAGES.map((l) => (
              <option key={l} value={l}>
                {LANGUAGE_LABELS[l]}
                {ENABLED_LANGUAGES.has(l) ? '' : ' (soon)'}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleRun}
            disabled={!canRun}
            title={actionTitle ?? 'Run against sample cases only — no submission'}
          >
            {running ? 'Running…' : 'Run'}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            title={actionTitle ?? 'Submit for full judging'}
            className="primary"
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
          <DraftStatus user={!!user} isSaving={isSaving} lastSavedAt={lastSavedAt} />
        </div>
        {submitError && <p className="form-error toolbar-error">{submitError}</p>}
        {runError && <p className="form-error toolbar-error">{runError}</p>}
        {!user && (
          <p className="editor-hint">
            You need to be signed in to run or submit. Drafts will be saved once you sign in.
          </p>
        )}
        <Editor
          height="60vh"
          language={monacoLanguage(language)}
          theme="vs-dark"
          value={code}
          onChange={(v) => setCode(v ?? '')}
          beforeMount={handleEditorBeforeMount}
          options={{
            minimap: { enabled: false },
            fontSize: 14,
            readOnly: isLoadingDraft,
            // Completion feels snappier with these tuned a bit from the
            // defaults; `quickSuggestions` turns on auto-trigger while
            // typing alphanumerics, matching VS Code's default behavior.
            quickSuggestions: { other: true, comments: false, strings: false },
            suggestOnTriggerCharacters: true,
            acceptSuggestionOnEnter: 'on',
          }}
        />
        {runResult && <RunResultPanel result={runResult} />}
      </section>
    </div>
  );
}

/**
 * Inline results panel shown below the editor after a successful /run call.
 * Displays per-sample-case pass/fail with full input/expected/actual since
 * all cases here are sample (visible) cases.
 */
function RunResultPanel({ result }: { result: RunSamplesResponse }) {
  const passCount = result.cases.filter((c) => c.passed).length;
  const total = result.cases.length;
  const allPassed = total > 0 && passCount === total;

  if (result.compileStderr) {
    return (
      <div className="run-results">
        <div className="run-results-header run-results--fail">
          <span className="verdict-badge verdict--compile_error">Compile Error</span>
        </div>
        <div className="stderr-block">
          <label>Compile output</label>
          <pre>{result.compileStderr}</pre>
        </div>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="run-results">
        <p className="empty">This problem has no sample cases to run against.</p>
      </div>
    );
  }

  return (
    <div className="run-results">
      <div
        className={`run-results-header ${allPassed ? 'run-results--pass' : 'run-results--fail'}`}
      >
        <strong>
          {passCount}/{total} sample {total === 1 ? 'case' : 'cases'} passed
        </strong>
        <span className="run-results-note">
          (Run only tests against visible samples. Click Submit to run all hidden tests.)
        </span>
      </div>
      {result.cases.map((c) => (
        <div key={c.index} className={`case-row case-row--${c.passed ? 'ok' : 'fail'}`}>
          <div className="case-row-head">
            <span className="case-label">Case #{c.index + 1}</span>
            <span className={`verdict-badge verdict--${c.verdict}`}>
              {VERDICT_LABELS[c.verdict]}
            </span>
            <span className="case-stats">
              {c.runtimeMs != null ? `${c.runtimeMs} ms` : '—'}
              {' · '}
              {c.memoryKb != null ? `${Math.round(c.memoryKb / 1024)} MB` : '—'}
            </span>
          </div>
          <div className="case-detail">
            <div className="sample-col">
              <label>Input</label>
              <pre>{c.input.trimEnd()}</pre>
            </div>
            <div className="sample-col">
              <label>Expected</label>
              <pre>{c.expected.trimEnd()}</pre>
            </div>
            <div className="sample-col">
              <label>Your output</label>
              <pre>{c.actual.trimEnd() || <em>(empty)</em>}</pre>
            </div>
          </div>
          {c.stderrSnippet && (
            <div className="stderr-block">
              <label>stderr</label>
              <pre>{c.stderrSnippet}</pre>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Small inline status for the draft persistence state.
 * Only shown when the user is signed in (otherwise drafts aren't saved at all).
 */
function DraftStatus({
  user,
  isSaving,
  lastSavedAt,
}: {
  user: boolean;
  isSaving: boolean;
  lastSavedAt: number | null;
}) {
  if (!user) return null;
  if (isSaving) return <span className="draft-status draft-status--saving">Saving…</span>;
  if (lastSavedAt !== null) {
    return <span className="draft-status draft-status--saved">Saved</span>;
  }
  return null;
}

function monacoLanguage(l: Language): string {
  // Monaco uses slightly different identifiers than our Language enum.
  if (l === 'cpp') return 'cpp';
  if (l === 'javascript') return 'javascript';
  if (l === 'typescript') return 'typescript';
  return l;
}
